import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, Search, Instagram, Facebook, Linkedin, Youtube, Globe, MoreVertical, Archive, ArchiveRestore, Trash2, ShieldAlert, GitMerge, AlertTriangle, CheckSquare, Square, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { StatusPill } from "../components/StatusPill";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import ClientForm from "../components/ClientForm";
import { formatMoney } from "../lib/currency";
import { servicesOf, CLIENT_SERVICES, canSeeCosts } from "../lib/services";

const TABS = [
  { key: "all", label: "All", param: "" },
  { key: "active", label: "Active", param: "", status: "Active" },
  { key: "onboarding", label: "Onboarding", param: "", status: "Onboarding" },
  { key: "paused", label: "Paused", param: "", status: "Paused" },
  { key: "archived", label: "Archived", param: "?only_archived=true" },
];

function fmtDate(iso) {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}
function daysSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.round((Date.now() - d.getTime()) / 86400000);
}

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("active");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [selected, setSelected] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [svc, setSvc] = useState("all");
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)).catch(() => {}); }, []);

  const isAdmin = ["admin", "ceo", "coo"].includes(user?.role);
  const load = async () => {
    try {
      const paramMap = Object.fromEntries(TABS.map(t => [t.key, t.param]));
      const { data } = await api.get(`/clients${paramMap[tab]}`);
      setClients(data);
      setSelected({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [tab === "archived"]);

  const filtered = useMemo(() => {
    const st = TABS.find(t => t.key === tab)?.status;
    return clients.filter(c =>
      (!st || c.status === st) &&
      (svc === "all" || servicesOf(c).includes(svc)) &&
      (!q || `${c.client_name} ${c.brand_name} ${c.industry}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [clients, q, tab, svc]);

  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected]);
  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const clearSelection = () => setSelected({});
  const selectAllVisible = () => setSelected(Object.fromEntries(filtered.map(c => [c.id, true])));

  const runBulk = async (action) => {
    if (!selectedIds.length) return;
    try {
      if (action === "archive") { await api.post("/clients/bulk-archive", { ids: selectedIds }); toast.success(`${selectedIds.length} client(s) archived`); }
      if (action === "delete") { await api.post("/clients/bulk-delete", { ids: selectedIds }); toast.success(`${selectedIds.length} client(s) deleted`); }
      setBulkAction(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const archiveOne = async (c) => { try { await api.post(`/clients/${c.id}/archive`); toast.success(`${c.client_name} archived`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unarchiveOne = async (c) => { try { await api.post(`/clients/${c.id}/unarchive`); toast.success(`${c.client_name} restored`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Directory</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Clients</h1>
          <p className="text-neutral-600 text-sm mt-1">{filtered.length} shown</p>
        </div>
        {isAdmin && (
          <Button onClick={()=>{ setEdit(null); setOpen(true); }} data-testid="create-client-button" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1"/> New Client
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:w-auto sm:max-w-md sm:flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search by name, brand, industry..." className="pl-9 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="client-search-input"/>
        </div>
        <div className="flex gap-1 flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`} className={`px-3 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider border transition-colors ${tab === t.key ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="service-filter">
        <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mr-1">Service</span>
        {["all", ...CLIENT_SERVICES].map(s => {
          const st = TABS.find(t => t.key === tab)?.status;
          const count = s === "all" ? null : clients.filter(c => (!st || c.status === st) && servicesOf(c).includes(s)).length;
          return (
            <button key={s} onClick={() => setSvc(s)} data-testid={`service-filter-${s === "all" ? "all" : s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${svc === s ? "bg-orange-500 border-orange-500 text-white font-semibold" : "bg-white border-[#E4E7EC] text-neutral-700 hover:border-orange-400"}`}>
              {s === "all" ? "All services" : s}{count !== null && <span className={`ml-1 ${svc === s ? "text-white/80" : "text-neutral-400"}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      {isAdmin && selectedIds.length > 0 && (
        <div className="panel p-3 flex items-center gap-3 flex-wrap sticky top-4 z-10 backdrop-blur-md bg-[#FFFFFF]/90 border-orange-500/30" data-testid="bulk-action-bar">
          <span className="text-[11px] font-mono uppercase tracking-wider text-orange-700">{selectedIds.length} selected</span>
          <button onClick={clearSelection} data-testid="bulk-clear" className="text-[10px] font-mono uppercase text-neutral-600 hover:text-neutral-900 flex items-center gap-1"><X className="w-3 h-3"/> Clear</button>
          <button onClick={selectAllVisible} data-testid="bulk-select-all" className="text-[10px] font-mono uppercase text-neutral-600 hover:text-neutral-900">Select all visible ({filtered.length})</button>
          <div className="flex-1"/>
          {tab !== "archived" && (
            <Button size="sm" variant="outline" className="border-yellow-500/40 text-yellow-700 hover:bg-yellow-500/10" onClick={()=>setBulkAction("archive")} data-testid="bulk-archive-btn">
              <Archive className="w-3.5 h-3.5 mr-1"/> Archive
            </Button>
          )}
          {selectedIds.length === 2 && (
            <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-700 hover:bg-orange-500/10" onClick={()=>setMergeOpen(true)} data-testid="bulk-merge-btn">
              <GitMerge className="w-3.5 h-3.5 mr-1"/> Merge
            </Button>
          )}
          <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10" onClick={()=>setBulkAction("delete")} data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1"/> Delete
          </Button>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full panel p-10 text-center text-neutral-500 text-sm">
            {tab === "archived" ? "No archived clients." : (clients.length === 0 ? "No clients yet." : "No clients match this filter.")}
          </div>
        )}
        {filtered.map((c) => {
          const isArchived = c.status === "Archived";
          const isSelected = !!selected[c.id];
          const idleDays = c.idle_flagged_at ? daysSince(c.idle_last_activity || c.idle_flagged_at) : null;
          return (
            <div key={c.id} className={`panel panel-hover p-5 group relative overflow-hidden ${isArchived ? "opacity-70" : ""} ${isSelected ? "ring-2 ring-orange-500/50" : ""}`} data-testid={`client-card-${c.id}`}>
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-orange-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity"/>
              <div className="flex items-start justify-between relative gap-2">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {isAdmin && (
                    <button onClick={()=>toggle(c.id)} data-testid={`select-${c.id}`} className="mt-1 shrink-0">
                      {isSelected ? <CheckSquare className="w-4 h-4 text-orange-400"/> : <Square className="w-4 h-4 text-neutral-500 hover:text-neutral-700"/>}
                    </button>
                  )}
                  <Link to={`/clients/${c.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-orange-500/30 to-red-500/20 border border-orange-500/20 grid place-items-center font-bold shrink-0">
                      {(c.brand_name||c.client_name||"?").slice(0,2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.client_name}</div>
                      <div className="text-[11px] font-mono uppercase text-neutral-500 truncate">{c.industry || "—"}</div>
                    </div>
                  </Link>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <StatusPill status={c.status}/>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded-md hover:bg-black/[0.04]" data-testid={`client-menu-${c.id}`}>
                          <MoreVertical className="w-4 h-4 text-neutral-600"/>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#FFFFFF] border-[#E4E7EC]">
                        <DropdownMenuItem onClick={()=>{ setEdit(c); setOpen(true); }} data-testid={`edit-client-${c.id}`} className="cursor-pointer">
                          <Pencil className="w-3.5 h-3.5 mr-2"/> Edit client
                        </DropdownMenuItem>
                        {!isArchived ? (
                          <DropdownMenuItem onClick={()=>archiveOne(c)} data-testid={`archive-client-${c.id}`} className="text-yellow-700 focus:bg-yellow-500/10 focus:text-yellow-800 cursor-pointer">
                            <Archive className="w-3.5 h-3.5 mr-2"/> Archive
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={()=>unarchiveOne(c)} data-testid={`unarchive-client-${c.id}`} className="text-emerald-700 focus:bg-emerald-500/10 focus:text-emerald-800 cursor-pointer">
                            <ArchiveRestore className="w-3.5 h-3.5 mr-2"/> Restore
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator className="bg-[#E4E7EC]"/>
                        <DropdownMenuItem onClick={()=>setDeleteTarget(c)} data-testid={`delete-client-${c.id}`} className="text-rose-700 focus:bg-rose-500/10 focus:text-rose-700 cursor-pointer">
                          <Trash2 className="w-3.5 h-3.5 mr-2"/> Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {idleDays !== null && !isArchived && (
                <div className="mt-3 flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-yellow-700" data-testid={`idle-flag-${c.id}`}>
                  <AlertTriangle className="w-3 h-3"/> Idle for {idleDays}+ days — consider archiving
                </div>
              )}

              <Link to={`/clients/${c.id}`} className="block">
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-mono text-neutral-600">
                  <div>Package: <span className="text-neutral-800">{c.package}</span></div>
                  {canSeeCosts(user) && <div data-testid={`client-card-retainer-${c.id}`}>Retainer: <span className="text-emerald-700">{formatMoney(c.monthly_retainer, c.currency)}</span>{c.one_time_total > 0 && <span className="text-neutral-500"> + {formatMoney(c.one_time_total, c.currency)} once</span>}</div>}
                  <div className="col-span-2">AM: <span className="text-neutral-800">{users.find(u => u.id === c.assigned_account_manager)?.name || c.account_manager || "—"}</span></div>
                  <div className="col-span-2 flex flex-wrap gap-1" data-testid={`client-card-services-${c.id}`}>
                    {servicesOf(c).map(s => <span key={s} className="px-1.5 py-0.5 rounded bg-orange-500/10 border border-orange-500/30 text-orange-700 text-[10px]">{s}</span>)}
                  </div>
                  {isArchived && (
                    <>
                      <div className="col-span-2">Archived: <span className="text-neutral-800">{fmtDate(c.archived_at)}</span></div>
                      <div className="col-span-2">Last active: <span className="text-neutral-800">{fmtDate(c.last_activity_at)}</span></div>
                    </>
                  )}
                  {!isArchived && c.last_activity_at && (
                    <div className="col-span-2">Last active: <span className="text-neutral-700">{fmtDate(c.last_activity_at)}</span></div>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2 text-neutral-500">
                  {c.instagram && <Instagram className="w-4 h-4"/>}
                  {c.facebook && <Facebook className="w-4 h-4"/>}
                  {c.linkedin && <Linkedin className="w-4 h-4"/>}
                  {c.youtube && <Youtube className="w-4 h-4"/>}
                  {c.website && <Globe className="w-4 h-4"/>}
                </div>
              </Link>
            </div>
          );
        })}
      </div>

      <ClientForm open={open} onOpenChange={setOpen} initial={edit} onSaved={load}/>
      <DeleteClientDialog target={deleteTarget} onClose={()=>setDeleteTarget(null)} onDeleted={load}/>
      <BulkConfirmDialog action={bulkAction} onClose={()=>setBulkAction(null)} count={selectedIds.length} onConfirm={()=>runBulk(bulkAction)}/>
      <MergeDialog open={mergeOpen} onOpenChange={setMergeOpen} clients={filtered.filter(c => selectedIds.includes(c.id))} onMerged={()=>{ setMergeOpen(false); load(); }}/>
    </div>
  );
}

function DeleteClientDialog({ target, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${target.id}`);
      toast.success(`${target.client_name} deleted`); onClose(); onDeleted?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-md" data-testid="delete-client-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-rose-700"><ShieldAlert className="w-5 h-5"/> Delete client permanently?</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            {target && <>Deleting <strong className="text-neutral-800">{target.client_name}</strong> will erase every post, upload, strategy, portal login, and request tied to them.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="text-[11px] font-mono text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded p-2">Consider Archive first — same effect but reversible.</div>
        <div className="text-[11px] font-mono text-rose-700 bg-rose-500/10 border border-rose-500/30 rounded p-2">This action cannot be undone.</div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="delete-client-cancel">Cancel</Button>
          <Button onClick={remove} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-neutral-900" data-testid="delete-client-confirm">
            {busy ? "Deleting..." : "Delete Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkConfirmDialog({ action, count, onClose, onConfirm }) {
  const isDelete = action === "delete";
  return (
    <Dialog open={!!action} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-md" data-testid="bulk-confirm-dialog">
        <DialogHeader>
          <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${isDelete ? "text-rose-700" : "text-yellow-700"}`}>
            {isDelete ? <ShieldAlert className="w-5 h-5"/> : <Archive className="w-5 h-5"/>}
            {isDelete ? `Delete ${count} client(s)?` : `Archive ${count} client(s)?`}
          </DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            {isDelete
              ? "Every content post, library upload, strategy, portal login, and request for these clients will be permanently erased."
              : "These clients will be hidden from the active list. You can restore them from the Archived tab anytime."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="bulk-confirm-cancel">Cancel</Button>
          <Button onClick={onConfirm} className={`text-neutral-900 ${isDelete ? "bg-rose-500 hover:bg-rose-600" : "bg-yellow-500 hover:bg-yellow-600"}`} data-testid="bulk-confirm-yes">
            {isDelete ? `Delete ${count}` : `Archive ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ open, onOpenChange, clients, onMerged }) {
  const [primaryId, setPrimaryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState({});
  useEffect(() => {
    if (open && clients.length === 2) {
      setPrimaryId(clients[0].id);
      Promise.all(clients.map(c => api.get(`/clients/${c.id}/merge-preview`).then(r => ({ id: c.id, data: r.data })).catch(() => ({ id: c.id, data: null }))))
        .then(res => setPreviews(Object.fromEntries(res.map(r => [r.id, r.data]))));
    }
  }, [open, clients]);
  const primary = clients.find(c => c.id === primaryId);
  const secondary = clients.find(c => c.id !== primaryId);
  const secondaryPreview = secondary ? previews[secondary.id] : null;
  const merge = async () => {
    if (!primary || !secondary) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${primary.id}/merge/${secondary.id}`);
      toast.success(`Merged — ${data.records_reassigned} record(s) moved to ${primary.client_name}`);
      onMerged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (clients.length !== 2) return null;
  const labelFor = (k) => ({contents:"posts", library_files:"library files", strategies:"strategies", requests:"requests", social_connections:"connections", notifications:"notifications", converted_leads:"linked leads", portal_users:"portal logins"}[k] || k);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-lg" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2"><GitMerge className="w-5 h-5 text-orange-400"/> Merge two clients</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            Pick the record you want to <strong className="text-orange-700">keep</strong>. All history from the other record will move to it. The other record is then removed.
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={primaryId} onValueChange={setPrimaryId} className="space-y-2">
          {clients.map(c => (
            <label key={c.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${primaryId === c.id ? "border-orange-500/50 bg-orange-500/10" : "border-[#E4E7EC] bg-[#FFFFFF] hover:border-neutral-600"}`} data-testid={`merge-pick-${c.id}`}>
              <RadioGroupItem value={c.id} id={`merge-${c.id}`}/>
              <div className="flex-1">
                <div className="text-sm font-medium">{c.client_name} <span className="text-[10px] font-mono text-neutral-500 ml-1">{c.industry}</span></div>
                <div className="text-[10px] font-mono text-neutral-500 uppercase">{c.package} · {formatMoney(c.monthly_retainer, c.currency)}/mo · Status: {c.status}</div>
              </div>
              {primaryId === c.id && <span className="text-[10px] font-mono uppercase text-orange-700 bg-orange-500/15 border border-orange-500/30 px-2 py-0.5 rounded">Keep</span>}
              {primaryId !== c.id && <span className="text-[10px] font-mono uppercase text-rose-700 bg-rose-500/10 border border-rose-500/30 px-2 py-0.5 rounded">Remove</span>}
            </label>
          ))}
        </RadioGroup>
        {secondaryPreview && (
          <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3" data-testid="merge-preview">
            <div className="text-[10px] font-mono uppercase tracking-wider text-orange-700 mb-2">Blast radius — will move to {primary?.client_name}</div>
            {secondaryPreview.total === 0 ? (
              <div className="text-xs text-neutral-600">No records tied to the removed client — safe to merge.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(secondaryPreview.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                  <span key={k} className="text-[10px] font-mono px-2 py-0.5 rounded border border-[#E4E7EC] bg-[#FFFFFF]" data-testid={`merge-preview-${k}`}>
                    <strong className="text-orange-700">{n}</strong> {labelFor(k)}
                  </span>
                ))}
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-500/30">
                  <strong>{secondaryPreview.total}</strong> total
                </span>
              </div>
            )}
          </div>
        )}
        <div className="text-[11px] font-mono text-yellow-700 bg-yellow-500/10 border border-yellow-500/30 rounded p-2">This action cannot be undone.</div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>onOpenChange(false)} data-testid="merge-cancel">Cancel</Button>
          <Button onClick={merge} disabled={busy || !primary || !secondary} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="merge-confirm">
            {busy ? "Merging..." : `Merge ${secondaryPreview ? `(${secondaryPreview.total} records)` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
